import { useEffect, useRef } from "react"
import { Award, Factory, Plus, Trash2, User } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"
import {
  type CouplagePlanColumn,
  type CouplageMatrix,
  couplageRowTotals,
  sourceKey,
} from "@/features/milling/lib/couplage-validation"
import { certificateTranslationKey } from "@/lib/certificates"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

// Editable Step 1 (couplage) plan table.
//
// Layout: rows are chargements (loads), columns are Lots Planned. Cells
// are bag counts the operator routes from each load into each lot.
// Three frozen columns on the left hold the chargement identity and the
// "remaining bags" indicator; one trailing static column hosts the
// "add Lot Planned" button. Each Lot Planned header shows the editable
// label + a remove button.
//
// Two footer rows summarise the plan: column totals (bags) and what's
// returning to Disponible per load (residual bags + estimated kg).
// There is intentionally NO weight row — weights belong to Step 2
// (fabrication), where the operator measures actual output.
interface CouplagePlanTableProps {
  usinage: UsinageDetail
  loads: CouplingLoadView[]
  columns: CouplagePlanColumn[]
  matrix: CouplageMatrix
  inputBags: number
  onUpdateColumn: (tempId: string, patch: Partial<CouplagePlanColumn>) => void
  onAddColumn: () => void
  onRemoveColumn: (tempId: string) => void
  // `sourceKey` is the composite "<sourceKind>:<id>" matrix row key.
  // Page-level handlers decode it to find the matching CouplingLoadView
  // when they need source-specific metadata (kind / cap).
  onSetCell: (sourceKey: string, tempId: string, bags: number) => void
  // Receives the source kind alongside the id so the page handler
  // dispatches `removeQueueLots` for QDLs vs `remove` for LOADs.
  onRemoveLoad?: (loadId: number, sourceKind: "LOAD" | "QUEUE_LOT") => void
  canAddColumn: boolean
  canRemoveLoad: boolean
  readOnly?: boolean
}

export function CouplagePlanTable({
  loads,
  columns,
  matrix,
  inputBags,
  onUpdateColumn,
  onAddColumn,
  onRemoveColumn,
  onSetCell,
  onRemoveLoad,
  canAddColumn,
  canRemoveLoad,
  readOnly = false,
}: CouplagePlanTableProps) {
  const { t } = useTranslation("milling")

  const rowTotals = couplageRowTotals(loads, matrix)

  // Auto-scroll to the new column whenever the operator adds one.
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const prevColumnCount = useRef(columns.length)
  useEffect(() => {
    if (columns.length > prevColumnCount.current && scrollerRef.current) {
      const el = scrollerRef.current
      requestAnimationFrame(() => {
        el.scrollTo({ left: el.scrollWidth, behavior: "smooth" })
      })
    }
    prevColumnCount.current = columns.length
  }, [columns.length])

  // First-run empty state.
  if (columns.length === 0 && !readOnly) {
    return (
      <Card className="flex flex-col items-center gap-4 p-10 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Factory className="size-6" />
        </span>
        <div className="max-w-md space-y-1.5">
          <h3 className="text-lg font-semibold tracking-tight">
            {t("detail.report.empty.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("detail.report.empty.bodyPlan", {
              bags: inputBags.toLocaleString(),
            })}
          </p>
        </div>
        <Button onClick={onAddColumn} disabled={!canAddColumn}>
          <Plus className="size-4" />
          {t("detail.report.empty.cta")}
        </Button>
      </Card>
    )
  }

  const COOP_W = 130
  const SLIP_W = 90
  const BAGS_W = 70
  const LEFT_COOP = 0
  const LEFT_SLIP = COOP_W
  const LEFT_BAGS = COOP_W + SLIP_W
  const stickyDividerShadow = "inset -1px 0 0 var(--border)"

  return (
    <Card className="min-w-0 overflow-hidden p-0">
      <div ref={scrollerRef} className="overflow-x-auto">
        <table
          className="text-sm"
          style={{ borderCollapse: "collapse", minWidth: "max-content" }}
        >
          <thead>
            <tr className="bg-muted/40">
              <th
                className="sticky bg-muted px-3 py-2 text-left text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
                style={{ left: LEFT_COOP, width: COOP_W, zIndex: 3 }}
              >
                {t("detail.report.th.cooperative")}
              </th>
              <th
                className="sticky bg-muted px-3 py-2 text-left text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
                style={{ left: LEFT_SLIP, width: SLIP_W, zIndex: 3 }}
              >
                {t("detail.report.th.deliverySlip")}
              </th>
              <th
                className="sticky bg-muted px-3 py-2 text-right text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
                style={{
                  left: LEFT_BAGS,
                  width: BAGS_W,
                  zIndex: 3,
                  boxShadow: stickyDividerShadow,
                }}
              >
                {t("detail.report.th.bags")}
              </th>
              {columns.map((c) => (
                <th
                  key={c.tempId}
                  className="border-l p-0 align-top"
                  style={{ minWidth: 160 }}
                >
                  <LotPlannedHeader
                    column={c}
                    onUpdate={(patch) => onUpdateColumn(c.tempId, patch)}
                    onRemove={() => onRemoveColumn(c.tempId)}
                    canRemove={!readOnly}
                    readOnly={readOnly}
                  />
                </th>
              ))}
              {!readOnly ? (
                <th
                  className="border-l bg-muted/40 p-0"
                  style={{ width: 44 }}
                >
                  <button
                    type="button"
                    onClick={onAddColumn}
                    disabled={!canAddColumn}
                    aria-label={t("detail.report.addLot")}
                    title={
                      canAddColumn
                        ? t("detail.report.addLot")
                        : t("detail.report.addLotFull")
                    }
                    className="inline-flex h-full min-h-9 w-full items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Plus className="size-4" />
                  </button>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loads.length === 0 ? (
              <tr>
                <Td
                  colSpan={3 + columns.length + (readOnly ? 0 : 1)}
                  className="py-6 text-center text-muted-foreground"
                >
                  {t("detail.report.noLoads")}
                </Td>
              </tr>
            ) : (
              loads.map((l) => {
                const key = sourceKey(l.sourceKind, l.loadId)
                const row = matrix[key] ?? {}
                const rowSum = rowTotals[key] ?? 0
                const cap = l.bagsAssigned
                const remainder = Math.max(0, cap - rowSum)
                const over = rowSum > cap
                // "under" is no longer an error in Step 1 — those bags
                // return to Disponible. We highlight the row only when
                // the operator over-routes.
                const rowTone = over ? "bg-destructive/5" : ""
                const stickyTone = over ? "bg-destructive/5" : "bg-card"

                return (
                  <tr
                    key={`${l.sourceKind}-${l.loadId}`}
                    className={cn("border-b", rowTone)}
                  >
                    <td
                      className={cn(
                        "sticky px-3 py-2 align-top",
                        stickyTone,
                      )}
                      style={{ left: LEFT_COOP, width: COOP_W, zIndex: 1 }}
                    >
                      <CoopCellContent load={l} />
                    </td>
                    <td
                      className={cn(
                        "sticky px-3 py-2 align-top",
                        stickyTone,
                      )}
                      style={{ left: LEFT_SLIP, width: SLIP_W, zIndex: 1 }}
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate font-mono text-xs underline decoration-muted-foreground/40 decoration-dotted underline-offset-4">
                              {l.sourceKind === "QUEUE_LOT"
                                ? (l.loadNumber ?? `#${l.loadId}`)
                                : (l.deliverySlip ??
                                    `#${l.loadNumber ?? l.loadId}`)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {l.sourceKind === "QUEUE_LOT"
                              ? t("detail.report.qdlIdTooltip")
                              : l.deliverySlip
                                ? t("detail.report.cccTooltip")
                                : t("detail.report.loadNumberTooltip")}
                          </TooltipContent>
                        </Tooltip>
                        {onRemoveLoad && canRemoveLoad && !readOnly ? (
                          <button
                            type="button"
                            onClick={() => onRemoveLoad(l.loadId, l.sourceKind)}
                            className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                            aria-label={t("detail.report.removeLoad")}
                            title={t("detail.report.removeLoad")}
                          >
                            <Trash2 className="size-3" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td
                      className={cn(
                        "sticky px-3 py-2 text-right align-top tabular-nums",
                        stickyTone,
                      )}
                      style={{
                        left: LEFT_BAGS,
                        width: BAGS_W,
                        zIndex: 1,
                        boxShadow: stickyDividerShadow,
                      }}
                    >
                      <div className="flex flex-col items-end leading-tight">
                        <span
                          className={cn(
                            "text-base font-semibold",
                            over
                              ? "text-destructive"
                              : remainder === 0
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-foreground",
                          )}
                        >
                          {over ? rowSum - cap : remainder}
                        </span>
                        <span
                          className={cn(
                            "text-[10px]",
                            over
                              ? "text-destructive"
                              : "text-muted-foreground",
                          )}
                        >
                          {over
                            ? t("detail.report.row.over", {
                                excess: rowSum - cap,
                              })
                            : remainder === 0
                              ? t("detail.report.row.allAssigned")
                              : t("detail.report.row.ofAssigned", { cap })}
                        </span>
                      </div>
                    </td>
                    {columns.map((c) => {
                      const v = row[c.tempId] ?? 0
                      const avgBagKg = l.avgBagKg ?? 0
                      const cellKg = v * avgBagKg
                      return (
                        <Td
                          key={c.tempId}
                          align="right"
                          className="border-l p-0"
                        >
                          <CouplageCell
                            value={v}
                            cap={cap}
                            cellKg={cellKg}
                            avgBagKg={avgBagKg}
                            readOnly={readOnly}
                            onChange={(n) => onSetCell(key, c.tempId, n)}
                          />
                        </Td>
                      )
                    })}
                    {!readOnly ? <Td className="border-l" /> : null}
                  </tr>
                )
              })
            )}
          </tbody>
          {loads.length > 0 ? (
            <tfoot>
              {/* Footer 1 — Total Bags row: inline arithmetic so the
                  operator sees exactly which chargements feed each Lot
                  Planned (e.g. "200 + 200 = 400"). Mirrors the spec
                  table in usinage-updates.md. */}
              <tr className="bg-muted/30">
                <td
                  className="bg-muted/60 sticky px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  style={{
                    left: LEFT_COOP,
                    width: COOP_W + SLIP_W + BAGS_W,
                    zIndex: 2,
                    boxShadow: stickyDividerShadow,
                  }}
                  colSpan={3}
                >
                  {t("detail.report.totals.totalBags")}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.tempId}
                    className="bg-muted/30 border-l p-2 align-middle"
                  >
                    <ArithmeticBagsCell
                      tempId={c.tempId}
                      loads={loads}
                      matrix={matrix}
                    />
                  </td>
                ))}
                {!readOnly ? <td className="bg-muted/30 border-l" /> : null}
              </tr>
              {/* Footer 2 — Estimated weight row, also inline. */}
              <tr className="bg-muted/20">
                <td
                  className="bg-muted/40 sticky px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  style={{
                    left: LEFT_COOP,
                    width: COOP_W + SLIP_W + BAGS_W,
                    zIndex: 2,
                    boxShadow: stickyDividerShadow,
                  }}
                  colSpan={3}
                >
                  {t("detail.report.totals.estimatedWeight")}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.tempId}
                    className="bg-muted/20 border-l p-2 align-middle"
                  >
                    <ArithmeticWeightCell
                      tempId={c.tempId}
                      loads={loads}
                      matrix={matrix}
                    />
                  </td>
                ))}
                {!readOnly ? <td className="bg-muted/20 border-l" /> : null}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </Card>
  )
}


// Stacks cooperative + customer + certification chips into the frozen
// left cell without growing its width. Queue de Lot rows render a
// distinctive badge instead — no cooperative, no certifications, no
// customer (those metadata fields don't survive the fabrication step).
function CoopCellContent({ load }: { load: CouplingLoadView }) {
  const { t } = useTranslation("milling")
  const certs = load.certifications ?? []
  const visibleCerts = certs.slice(0, 2)
  const overflow = certs.length - visibleCerts.length

  if (load.sourceKind === "QUEUE_LOT") {
    return (
      <div className="flex min-w-0 flex-col gap-1 leading-tight">
        <Badge
          variant="outline"
          className="w-fit rounded bg-amber-500/10 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400"
        >
          {t("detail.report.row.queueLotBadge")}
        </Badge>
        <span className="block truncate font-medium font-mono text-[11px]">
          {load.loadNumber ?? `#${load.loadId}`}
        </span>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-col gap-1 leading-tight">
      <span className="block truncate font-medium">
        {load.cooperativeName ?? "—"}
      </span>
      {load.customerName ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 text-[10px]">
              <User className="size-2.5 flex-shrink-0" />
              <span className="truncate">{load.customerName}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("detail.report.row.customerLabel")}: {load.customerName}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {certs.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex min-w-0 flex-wrap items-center gap-1 overflow-hidden">
              <Award className="text-muted-foreground size-2.5 flex-shrink-0" />
              {visibleCerts.map((cert) => {
                const key = certificateTranslationKey(cert)
                const label = key ? t(key, cert) : cert
                return (
                  <Badge
                    key={cert}
                    variant="secondary"
                    className="max-w-full truncate px-1 py-0 text-[9px] font-normal leading-tight"
                  >
                    {label}
                  </Badge>
                )
              })}
              {overflow > 0 ? (
                <span className="text-muted-foreground text-[9px] tabular-nums">
                  {t("detail.report.row.certificationsMore", { n: overflow })}
                </span>
              ) : null}
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">
            <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
              {t("detail.report.row.certificationsLabel")}
            </div>
            <ul className="mt-1 flex flex-col gap-0.5 text-xs">
              {certs.map((cert) => {
                const key = certificateTranslationKey(cert)
                const label = key ? t(key, cert) : cert
                return <li key={cert}>{label}</li>
              })}
            </ul>
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

function CouplageCell({
  value,
  cap,
  cellKg,
  avgBagKg,
  readOnly,
  onChange,
}: {
  value: number
  cap: number
  cellKg: number
  avgBagKg: number
  readOnly: boolean
  onChange: (n: number) => void
}) {
  const { t } = useTranslation("milling")
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative">
          <Input
            type="number"
            min={0}
            max={cap}
            value={value || ""}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
            onFocus={(e) => e.target.select()}
            placeholder="—"
            readOnly={readOnly}
            className="h-9 w-full rounded-none border-0 bg-transparent text-right tabular-nums focus-visible:bg-muted/40 focus-visible:ring-0"
          />
        </div>
      </TooltipTrigger>
      {value > 0 && avgBagKg > 0 ? (
        <TooltipContent>
          {t("detail.report.cell.kgTooltip", {
            bags: value,
            avg: formatNumber(avgBagKg, 2),
            kg: formatNumber(cellKg, 2),
          })}
        </TooltipContent>
      ) : null}
    </Tooltip>
  )
}

function LotPlannedHeader({
  column,
  onUpdate,
  onRemove,
  canRemove,
  readOnly,
}: {
  column: CouplagePlanColumn
  onUpdate: (patch: Partial<CouplagePlanColumn>) => void
  onRemove: () => void
  canRemove: boolean
  readOnly: boolean
}) {
  const { t } = useTranslation("milling")

  return (
    <div className="flex items-center gap-1 p-2">
      <Input
        type="text"
        value={column.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        readOnly={readOnly}
        placeholder={t("detail.report.column.lotPlannedLabel")}
        className="h-7 text-center font-mono text-xs"
      />
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
          title={t("detail.report.column.remove")}
          aria-label={t("detail.report.column.remove")}
        >
          <Trash2 className="size-3" />
        </button>
      ) : null}
    </div>
  )
}

// ArithmeticBagsCell renders just the column total ("400"); the
// summand-by-summand breakdown ("200 + 200 = 400") is moved to a
// hover tooltip so the footer reads cleanly. Empty columns show
// "0" with no tooltip.
function ArithmeticBagsCell({
  tempId,
  loads,
  matrix,
}: {
  tempId: string
  loads: CouplingLoadView[]
  matrix: CouplageMatrix
}) {
  const terms = loads
    .map((l) => ({
      label:
        l.sourceKind === "QUEUE_LOT"
          ? (l.loadNumber ?? `#${l.loadId}`)
          : (l.deliverySlip ?? `#${l.loadNumber ?? l.loadId}`),
      bags:
        Number(matrix[sourceKey(l.sourceKind, l.loadId)]?.[tempId]) || 0,
    }))
    .filter((x) => x.bags > 0)

  if (terms.length === 0) {
    return (
      <div className="text-center text-xs font-semibold tabular-nums text-muted-foreground">
        0
      </div>
    )
  }

  const total = terms.reduce((a, n) => a + n.bags, 0)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help text-center text-xs font-semibold tabular-nums text-emerald-700 underline decoration-muted-foreground/30 decoration-dotted underline-offset-4 dark:text-emerald-400">
          {formatNumber(total)}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="flex flex-col gap-1 text-xs tabular-nums">
          {terms.map((term, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-[10px] text-muted-foreground">
                {term.label}
              </span>
              <span>{formatNumber(term.bags)}</span>
            </div>
          ))}
          <div className="mt-1 flex items-baseline justify-between gap-3 border-t pt-1 font-semibold">
            <span>=</span>
            <span>{formatNumber(total)}</span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

// ArithmeticWeightCell renders just the total estimated weight in MT
// ("8.73 MT"); the per-source breakdown
// ("200×85.5 = 17 100 + 200×80 = 16 000 = 33 100 kg") lives in a
// hover tooltip. Operators see whether the lot covers the
// 24.5–25.5 MT window at a glance, with the math one hover away.
function ArithmeticWeightCell({
  tempId,
  loads,
  matrix,
}: {
  tempId: string
  loads: CouplingLoadView[]
  matrix: CouplageMatrix
}) {
  const { t } = useTranslation("milling")
  const terms = loads
    .map((l) => {
      const bags =
        Number(matrix[sourceKey(l.sourceKind, l.loadId)]?.[tempId]) || 0
      if (bags <= 0) return null
      const avg = l.avgBagKg ?? 0
      const label =
        l.sourceKind === "QUEUE_LOT"
          ? (l.loadNumber ?? `#${l.loadId}`)
          : (l.deliverySlip ?? `#${l.loadNumber ?? l.loadId}`)
      return { label, bags, avg, kg: bags * avg }
    })
    .filter(
      (x): x is { label: string; bags: number; avg: number; kg: number } =>
        x !== null,
    )

  if (terms.length === 0) {
    return (
      <div className="text-center text-[11px] tabular-nums text-muted-foreground">
        —
      </div>
    )
  }

  const total = terms.reduce((a, term) => a + term.kg, 0)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help text-center text-xs font-semibold tabular-nums underline decoration-muted-foreground/30 decoration-dotted underline-offset-4">
          {formatNumber(kgToMt(total), 3)}{" "}
          <span className="text-[10px] font-normal text-muted-foreground">
            MT
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">
        <div className="flex flex-col gap-1 text-xs tabular-nums">
          {terms.map((term, i) => (
            <div key={i} className="flex items-baseline justify-between gap-3">
              <span className="font-mono text-[10px] text-muted-foreground">
                {term.label}
              </span>
              <span>
                {term.bags}×{formatNumber(term.avg, 2)} ={" "}
                {formatNumber(term.kg, 2)} kg
              </span>
            </div>
          ))}
          <div className="mt-1 flex items-baseline justify-between gap-3 border-t pt-1 font-semibold">
            <span>
              {t("detail.report.totals.estimatedWeightTotalShort")}
            </span>
            <span>
              {formatNumber(total, 2)} kg · {formatNumber(kgToMt(total), 3)} MT
            </span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}


function Td({
  children,
  align,
  colSpan,
  className,
}: {
  children?: React.ReactNode
  align?: "right"
  colSpan?: number
  className?: string
}) {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        "px-3 py-2",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
    >
      {children}
    </td>
  )
}
